'use client';

import styles from './addPlaceholderTile.module.scss';

export type AddPlaceholderTileProps = {
  /** Label text (e.g., "Add Waitlist", "Add Litter") */
  label: string;
  /** Called when the placeholder is clicked */
  onClick: () => void;
  /** Whether to show the button in the footer (desktop only) */
  showButton?: boolean;
  /** Optional button text (defaults to label) */
  buttonText?: string;
  /** Whether the placeholder is disabled */
  disabled?: boolean;
};

/**
 * AddPlaceholderTile - Dashed "Add X" card for tile grids.
 *
 * Displays a clickable placeholder card with a plus sign and label.
 * Responsive: shows as full-width row on mobile, tall card on desktop.
 *
 * @example
 * ```tsx
 * <AddPlaceholderTile
 *   label="Add Waitlist"
 *   onClick={() => setIsModalOpen(true)}
 * />
 * ```
 */
export function AddPlaceholderTile({
  label,
  onClick,
  showButton = true,
  buttonText,
  disabled = false
}: AddPlaceholderTileProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      className={`${styles.placeholder} ${disabled ? styles.disabled : ''}`}
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      aria-disabled={disabled}
    >
      <div className={styles.placeholderLabel}>
        <span className={styles.plusSign}>+</span>
        {label}
      </div>
      {showButton && (
        <div className={styles.placeholderBtnContainer}>
          <span className={styles.placeholderBtn}>{buttonText ?? label}</span>
        </div>
      )}
    </div>
  );
}
